// Module 4 — List Your Expenses
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import Heading from '@ramsey-design-system/heading';
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import QrCodePrompt from '../QrCodePrompt';

// Utils
import { PATHS } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { WorkshopActionItemDefinition } from '../../types';

function ListYourExpensesClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-expenses.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-expenses.jpg"
    />
  );
}

function WhatAreExpenses() {
  return (
    <div className="flex flex-col gap-4">
      <Text className="text-4 leading-lg">
        Expenses are everything you spend money on each month, both the bills
        you expect and the costs you might forget. Think insurance,
        subscriptions, clothing, dining out, and everything in between.
      </Text>
      <ul className="text-4 leading-lg ml-2 list-disc">
        <li>Insurance</li>
        <li>Subscriptions</li>
        <li>Clothing</li>
        <li>Dining out</li>
        <li>Entertainment</li>
        <li>Personal care</li>
        <li>Childcare</li>
        <li>Medical copays</li>
        <li>Gym membership</li>
      </ul>
    </div>
  );
}

function ListYourExpensesActivity() {
  const { workshopId } = useParams() as { workshopId: string };
  const activityUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.BUDGET_EXERCISE(workshopId)}`;

  return (
    <QrCodePrompt
      activityUrl={activityUrl}
      slug="list-your-expenses-activity"
      dialogTitle="List Your Expenses Activity QR Code"
      linkLabel="Budget Activity"
    >
      <Text className="text-5 text-blue-90 leading-lg font-bold">
        Scan the QR code to add your expenses to your budget!
      </Text>
      <Text className="text-4 leading-lg">
        For this activity, focus on listing your expenses. Don&apos;t worry if
        you don&apos;t know the exact amounts; your best guess is a great place
        to start. Hold off on listing your debt, as we will cover that in the
        next section.
      </Text>
      <div className="border-gray-20 mt-1 overflow-hidden rounded-[20px] border bg-white p-5">
        <img
          src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/expenses-animation.gif"
          alt="Step 3 of 5, List Your Expenses"
          className="h-auto w-full [clip-path:inset(0.25%)]"
        />
      </div>
    </QrCodePrompt>
  );
}

function WaysToTrimYourExpenses() {
  return (
    <div className="flex flex-col gap-4">
      <Text className="text-4 leading-lg">
        Small changes in how you spend add up faster than you think.
      </Text>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Food
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Meal plan and pack your lunch</li>
          <li>Buy generic brands</li>
          <li>Cut restaurants, especially if you&apos;re in debt</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Subscriptions and Bills
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Cancel unused streaming services and subscriptions</li>
          <li>Shop around for better cell phone and insurance rates</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Lifestyle and Habits
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Unsubscribe from retail emails to avoid impulse purchases</li>
          <li>
            Wait 24 hours before buying anything that wasn&apos;t in your budget
          </li>
          <li>Bring cash when you go out — it&apos;s harder to overspend</li>
          <li>
            Find free or low-cost alternatives for entertainment and date nights
          </li>
        </ul>
      </div>
    </div>
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: "What is one spending habit you know you could cut right now but haven't?"
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'What expense surprised you most when you actually wrote it down?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: 'If you had to cut $500 from your monthly expenses starting tomorrow, where would you start?'
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  {
    title: 'List Your Expenses',
    Content: ListYourExpensesClip,
    icon: VideoOutlinedIcon,
    duration: '2:21'
  },
  { title: 'What are Expenses?', Content: WhatAreExpenses },
  { title: 'List Your Expenses Activity', Content: ListYourExpensesActivity },
  { title: 'Ways to Trim Your Expenses', Content: WaysToTrimYourExpenses },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
